import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/**
 * The little bit of state the FAB and the home hero have to share.
 *
 * The FAB is the second door to the conversational surface, and the rule from
 * Jorge's DY1.4 spec is that only one door is open at a time: while the visitor
 * can see the hero's composer, the FAB stays away.
 *
 * **What is observed is the COMPOSER, not the hero section** (DY1.6-UX). It used
 * to be the whole `<section>`, with the FAB appearing once less than 8% of it
 * remained on screen — and a ratio of a section's own height is a different
 * amount of scrolling on every width. The home hero stacks on a phone: mark,
 * headline, sub-head, composer, two chip groups, the provider block. That makes
 * the section roughly half again as tall as it is on a desktop, so "8% left"
 * arrived hundreds of pixels later, long after the composer itself had gone.
 * The composer leaving the viewport is the thing the rule was always about, so
 * it is now the thing that is measured.
 *
 * `isIntersecting` rather than a ratio, for the same reason: a threshold
 * expressed as a fraction of the observed element is a threshold that moves with
 * layout. "Any part of the box is on screen" is the same statement at 390px and
 * at 1280px.
 */
const DanyUiContext = createContext(null);

export function DanyUiProvider({ children }) {
  // Null means "this page has no conversational box" — a classic page — and the
  // FAB is then always present.
  const [boxEl, setBoxEl] = useState(null);
  const [boxVisible, setBoxVisible] = useState(false);
  const observerRef = useRef(null);

  const registerConversationBox = useCallback((element) => {
    setBoxEl(element);
    // A page that unmounts its hero (or navigates away) has no box again.
    if (!element) setBoxVisible(false);
  }, []);

  React.useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!boxEl) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      // No observer (very old browser, or a test environment): treat the box as
      // gone, so the FAB is present rather than unreachable.
      setBoxVisible(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setBoxVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(boxEl);
    observerRef.current = observer;
    setBoxVisible(true);
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [boxEl]);

  const value = useMemo(
    () => ({
      registerConversationBox,
      hasConversationBox: !!boxEl,
      conversationBoxVisible: boxVisible,
    }),
    [registerConversationBox, boxEl, boxVisible]
  );
  return <DanyUiContext.Provider value={value}>{children}</DanyUiContext.Provider>;
}

export function useDanyUi() {
  const ctx = useContext(DanyUiContext);
  if (!ctx) throw new Error("useDanyUi must be used inside DanyUiProvider");
  return ctx;
}

export default DanyUiContext;
